import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, Users as UsersIcon, ShieldCheck, Save, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ApprovalWorkflow = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const isSuperAdmin = hasRole(user, 'super_admin');
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [drafts, setDrafts] = useState({}); // { entity_type: { approver_user_ids: [], approver_role_keys: [], enabled: bool } }
  const [saving, setSaving] = useState('');

  const load = useCallback(async () => {
    const a = await api.get('/approval-workflow'); setAssignments(a.data);
    const u = await api.get('/users'); setUsers(u.data);
    const r = await api.get('/roles'); setRoles(r.data);
    const d = {};
    a.data.forEach((row) => { d[row.entity_type] = { approver_user_ids: [...(row.approver_user_ids || [])], approver_role_keys: [...(row.approver_role_keys || [])], enabled: !!row.enabled }; });
    setDrafts(d);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!isSuperAdmin) return <div className="p-4 text-sm text-slate-600">Only Super Admin can manage the approval workflow.</div>;

  const updateDraft = (entity, patch) => setDrafts((p) => ({ ...p, [entity]: { ...p[entity], ...patch } }));
  const toggleUser = (entity, userId) => {
    const ids = new Set(drafts[entity]?.approver_user_ids || []);
    ids.has(userId) ? ids.delete(userId) : ids.add(userId);
    updateDraft(entity, { approver_user_ids: Array.from(ids) });
  };
  const toggleRoleKey = (entity, roleKey) => {
    const keys = new Set(drafts[entity]?.approver_role_keys || []);
    keys.has(roleKey) ? keys.delete(roleKey) : keys.add(roleKey);
    updateDraft(entity, { approver_role_keys: Array.from(keys) });
  };

  const isDirty = (entity) => {
    const orig = assignments.find((a) => a.entity_type === entity);
    const d = drafts[entity] || {};
    if (!orig) return true;
    return (
      orig.enabled !== d.enabled ||
      JSON.stringify((orig.approver_user_ids || []).slice().sort()) !== JSON.stringify((d.approver_user_ids || []).slice().sort()) ||
      JSON.stringify((orig.approver_role_keys || []).slice().sort()) !== JSON.stringify((d.approver_role_keys || []).slice().sort())
    );
  };

  const save = async (entity) => {
    setSaving(entity);
    try {
      await api.put(`/approval-workflow/${entity}`, drafts[entity]);
      toast({ title: 'Approval rule saved' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(''); }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-600">
        Designate which users (or entire roles) are responsible for approving each type of submission. Designated approvers receive notifications when a new item awaits their approval.
      </div>
      {assignments.map((a) => {
        const d = drafts[a.entity_type] || { approver_user_ids: [], approver_role_keys: [], enabled: true };
        return (
          <Card key={a.entity_type}>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    <h3 className="text-lg font-semibold text-slate-900">{a.label}</h3>
                    {d.enabled ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Enabled</Badge> : <Badge variant="secondary">Disabled</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{a.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-sm">
                    <Switch checked={d.enabled} onCheckedChange={(c) => updateDraft(a.entity_type, { enabled: c })} />
                    Active
                  </label>
                  <Button size="sm" onClick={() => save(a.entity_type)} disabled={!isDirty(a.entity_type) || saving === a.entity_type} className="bg-emerald-600 hover:bg-emerald-700">
                    {saving === a.entity_type ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Saving</> : <><Save className="h-4 w-4 mr-1.5" /> Save</>}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-2"><ShieldCheck className="h-3.5 w-3.5" /> Approver Roles ({d.approver_role_keys.length})</div>
                  <div className="border rounded-md p-2 max-h-44 overflow-y-auto space-y-1">
                    {roles.map((r) => (
                      <label key={r.id} className="flex items-center gap-2 text-sm py-1 px-1.5 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox checked={d.approver_role_keys.includes(r.key)} onCheckedChange={() => toggleRoleKey(a.entity_type, r.key)} />
                        <span className="flex-1">{r.label}</span>
                        <span className="text-[10px] text-slate-400 font-mono">{r.key}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-2"><UsersIcon className="h-3.5 w-3.5" /> Specific Approvers ({d.approver_user_ids.length})</div>
                  <div className="border rounded-md p-2 max-h-44 overflow-y-auto space-y-1">
                    {users.filter((u) => u.is_active).map((u) => (
                      <label key={u.id} className="flex items-center gap-2 text-sm py-1 px-1.5 rounded hover:bg-slate-50 cursor-pointer">
                        <Checkbox checked={d.approver_user_ids.includes(u.id)} onCheckedChange={() => toggleUser(a.entity_type, u.id)} />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">{u.full_name}</div>
                          <div className="text-[10px] text-slate-400 font-mono">{u.username} • {u.role}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default ApprovalWorkflow;
